import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import Layout from '../UI/introGameLayout/layout';

import { generalInfoQuery, scenarioLevelsQuery } from './api';

import IconCorrect from '../UI/introGameLayout/explanation/images/icon-correct.svg';
import IconIncorrect from '../UI/introGameLayout/explanation/images/icon-incorrect.svg';

import './tutorial.css';

// Hardcoded tutorial content per scenario level name. Each tutorial has 2
// decisions; each decision has 2 date-based options. The option whose
// `isCorrect` is true is the correct answer for that decision.
const tutorials = {
  'revenue-recognition': {
    title: 'Revenue Recognition',
    highlight: 'Revenue Recognition',
    explanation: 'revenue should be recognized when earned (i.e. when goods or services are provided to the customer). Click Continue.',
    decisions: [
      {
        subtitle: 'You run a Pizza Shop business and you use accrual accounting. Select the date when you can record sales (i.e. revenues)?',
        options: [
          {
            label: 'Day 1',
            description: (
              <>
                <strong>Customer A </strong>
                <strong>paid $1,000</strong>
                {' for pizza to be '}
                <strong>delivered next month</strong>
                {'.'}
              </>
            ),
            isCorrect: false,
          },
          {
            label: 'Day 45',
            description: (
              <>
                {'You '}
                <strong>delivered the pizza</strong>
                {' to Customer A.'}
              </>
            ),
            isCorrect: true,
          },
        ],
      },
      {
        subtitle: 'You run a Pizza Shop business and you use accrual accounting for reporting financial results. Select the date when you can record sales (i.e. revenues)?',
        options: [
          {
            label: 'Current Month',
            description: (
              <>
                {'You '}
                <strong>delivered $500 worth of pizza to Customer B</strong>
                {', but the '}
                <strong>customer will pay later</strong>
                {'.'}
              </>
            ),
            isCorrect: true,
          },
          {
            label: 'Next Month',
            description: (
              <>
                <strong>Customer B pays for the $500 pizza</strong>
                {' that you delivered on Day 5.'}
              </>
            ),
            isCorrect: false,
          },
        ],
      },
    ],
  },
  'expense-recognition': {
    title: 'Expense Recognition',
    highlight: 'Expense Recognition',
    explanation: 'expense should be recognized when incurred (i.e. when goods or services are used or consumed by the company). Click Continue.',
    decisions: [
      {
        subtitle: 'You run a Coffee Shop business and you use accrual accounting for reporting financial results. Select the date when you can record an Expense?',
        options: [
          {
            label: 'Day 1',
            description: (
              <>
                <strong>You prepaid $36,000</strong>
                {' to '}
                <strong>cover the next 12 months</strong>
                {' of rent (coffee shop).'}
              </>
            ),
            isCorrect: false,
          },
          {
            label: 'Day 365',
            description: (
              <>
                <strong>The next 12 months have passed </strong>
                {'and '}
                <strong>you\u2019ve used the Coffee Shop space</strong>
                {'.'}
              </>
            ),
            isCorrect: true,
          },
        ],
      },
      {
        subtitle: 'You run a Coffee Shop business and you use accrual accounting for reporting financial results. Select the date when you can record an Expense?',
        options: [
          {
            label: 'Current Month',
            description: (
              <>
                {'You owe your Employee A $1,500 for '}
                <strong>work performed during the month</strong>
                {'.'}
              </>
            ),
            isCorrect: true,
          },
          {
            label: 'Next Month',
            description: (
              <>
                <strong>You paid Employee A</strong>
                {' $1,500 for work performed in the prior month.'}
              </>
            ),
            isCorrect: false,
          },
        ],
      },
    ],
  },
};

export default function ThePrinciplesTutorial() {
  const navigate = useNavigate();
  const params = useParams();
  const levelNumber = Number(params.levelNumber);

  const { getCurrentThePrinciplesGameId, getUser } = useAuth();
  const gameId = getCurrentThePrinciplesGameId();
  const user = getUser();

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const game = generalInfo?.games?.find(
    g => g.id === gameId && g.game_type.includes('The Principles')
  );
  const scenarioId = game?.scenario;

  const { data: scenarioLevels } = useQuery({
    ...scenarioLevelsQuery({ scenarioId }),
    enabled: !!scenarioId,
  });

  const levelName = useMemo(() => {
    if (!scenarioLevels?.length) return null;
    return scenarioLevels.find(l => l.level === levelNumber)?.name || null;
  }, [scenarioLevels, levelNumber]);

  const tutorial = levelName ? tutorials[levelName] : null;

  // True once we know whether this user has already touched this level
  // (any status besides null / 'not_started' on any session for this game).
  const hasLevelProgress = useMemo(() => {
    if (!generalInfo?.sessions || !gameId || !user?.id) return false;
    return generalInfo.sessions.some(session =>
      session.game === gameId &&
      session.user === user.id &&
      session[`level${levelNumber}`] != null &&
      session[`level${levelNumber}`] !== 'not_started'
    );
  }, [generalInfo?.sessions, gameId, user?.id, levelNumber]);

  const [decisionIndex, setDecisionIndex] = useState(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [isClosingVisible, setIsClosingVisible] = useState(false);
  const [isSecondNoteVisible, setIsSecondNoteVisible] = useState(false);

  // If the user already has progress on this level, skip the tutorial and
  // drop them straight into the game. Guards against direct URL hits, back
  // navigation, and cases where the upstream Transition router missed it.
  if (hasLevelProgress) {
    navigate(`/the-principles/level/${levelNumber}`, { replace: true });
    return null;
  }

  // If scenario levels have loaded but this level name has no matching
  // tutorial (e.g. "revenue-expense"), skip straight to the game.
  if (scenarioLevels?.length && !tutorial) {
    navigate(`/the-principles/level/${levelNumber}`, { replace: true });
    return null;
  }

  if (!tutorial) {
    return null;
  }

  // Closing-page copy is dynamic based on the scenario level name.
  const closingTitle =
    levelName === 'revenue-recognition' ? 'Revenue Recognition' :
    levelName === 'expense-recognition' ? 'Expense Recognition' :
    'Revenue & Expense Recognition Principles';

  const closingSubject =
    levelName === 'revenue-recognition' ? 'Revenues' :
    levelName === 'expense-recognition' ? 'Expenses' :
    'Revenues and Expenses';

  const decision = tutorial.decisions[decisionIndex];
  const selectedOption = selectedOptionIndex != null ? decision.options[selectedOptionIndex] : null;
  const isAnswered = selectedOption !== null;
  const isLastDecision = decisionIndex === tutorial.decisions.length - 1;

  const handleOptionClick = (optionIndex) => {
    if (isAnswered) return;
    setSelectedOptionIndex(optionIndex);
  };

  const handleContinueClick = () => {
    if (isLastDecision) {
      setIsClosingVisible(true);
    } else {
      setDecisionIndex(decisionIndex + 1);
      setSelectedOptionIndex(null);
    }
  };

  const handleClosingContinueClick = () => {
    if (!isSecondNoteVisible) {
      setIsSecondNoteVisible(true);
    } else {
      navigate(`/the-principles/level/${levelNumber}`);
    }
  };

  if (isClosingVisible) {
    return (
      <Layout
        isHeaderVisible={true}
        isFooterVisible={false}
        backgroundColor="rgba(0, 27, 59, 0.04)"
        className="principles-tutorial-layout"
      >
        <div className="principles-tutorial principles-tutorial-closing">
          <h1 className="principles-tutorial-title">{closingTitle}</h1>
          <p className="principles-tutorial-closing-subtitle">You’ve Completed Tutorial!</p>

          <div className="principles-tutorial-closing-note">
            <p>
              {'Knowing when to record '}
              <strong>{closingSubject}</strong>
              {' under accrual accounting is critical because it directly shapes what a company reports on its Income Statement, especially its profitability which investors and other stakeholders closely evaluate.'}
            </p>
          </div>

          {isSecondNoteVisible && (
            <div className="principles-tutorial-closing-note">
              <p>
                <strong>In this level of the exercise</strong>
                {', you’re going to review a series of transactions and determine if '}
                <strong>{closingSubject}</strong>
                {' should be recorded. Essentially, you’re going to decide if the transaction immediately impact profits.'}
              </p>
            </div>
          )}

          <div className="principles-tutorial-footer">
            <button
              type="button"
              className="principles-tutorial-continue"
              onClick={handleClosingContinueClick}
            >
              Continue
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      isHeaderVisible={true}
      isFooterVisible={false}
      backgroundColor="rgba(0, 27, 59, 0.04)"
      className="principles-tutorial-layout"
    >
      <div className="principles-tutorial">
        <h1 className="principles-tutorial-title">{tutorial.title}</h1>
        <p className="principles-tutorial-subtitle">{decision.subtitle}</p>

        <div className="principles-tutorial-options">
          <div className="principles-tutorial-connector" />
          {decision.options.map((option, idx) => {
            const isSelected = selectedOptionIndex === idx;
            const selectionClass = isSelected
              ? (option.isCorrect ? 'selected-correct' : 'selected-incorrect')
              : '';
            return (
              <button
                key={idx}
                type="button"
                className={`principles-tutorial-option ${selectionClass}`}
                onClick={() => handleOptionClick(idx)}
                disabled={isAnswered}
              >
                <div className="principles-tutorial-option-label">{option.label}</div>
                <div className="principles-tutorial-option-description">{option.description}</div>
              </button>
            );
          })}
        </div>

        {isAnswered && (
          <div className={`principles-tutorial-feedback ${selectedOption.isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="principles-tutorial-feedback-heading">
              <img
                src={selectedOption.isCorrect ? IconCorrect : IconIncorrect}
                alt={selectedOption.isCorrect ? 'Correct' : 'Incorrect'}
              />
              <span>{selectedOption.isCorrect ? 'Correct!' : 'Incorrect'}</span>
            </div>
            <p>
              {'Under the '}
              <strong className="principles-tutorial-feedback-highlight">{tutorial.highlight}</strong>
              {' principles, '}
              {tutorial.explanation}
            </p>
          </div>
        )}

        {isAnswered && (
          <div className="principles-tutorial-footer">
            <button
              type="button"
              className="principles-tutorial-continue"
              onClick={handleContinueClick}
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
